import type { ReactNode } from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import { GEMINI_GRADIENT } from '../../theme';

type Starter = {
  icon: ReactNode;
  title: string;
  hint: string;
  prompt: string;
};

/**
 * Starters map to what the assistant can actually do (ops stats, ops search,
 * single-application Q&A) so a cold start never leaves a reviewer guessing.
 */
const STARTERS: Starter[] = [
  {
    icon: <FilterAltRoundedIcon fontSize="small" />,
    title: 'Segregate applications',
    hint: 'Filters the Applications page for you',
    prompt: 'Show applications stuck at income verification this week',
  },
  {
    icon: <SummarizeRoundedIcon fontSize="small" />,
    title: 'Summarize the queue',
    hint: 'Counts and breakdowns across a date range',
    prompt: 'How many applications came in today, broken down by status?',
  },
  {
    icon: <PersonSearchRoundedIcon fontSize="small" />,
    title: 'Review one application',
    hint: 'Look up by name, email, or application id',
    prompt: 'Tell me about the latest declined application and why it was declined',
  },
];

export function LoanOpsChatEmptyState({
  onPick,
  compact,
}: {
  onPick: (prompt: string) => void;
  /** Narrow/short panel — drop the hero so starters stay above the fold. */
  compact?: boolean;
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  return (
    <Stack spacing={1.5} sx={{ py: compact ? 1 : 2.5, px: 0.5 }}>
      {!compact && (
        <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center', px: 1 }}>
          <Box
            aria-hidden
            sx={{
              width: 44,
              height: 44,
              borderRadius: '14px',
              background: GEMINI_GRADIENT,
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
              fontWeight: 800,
              fontSize: '0.85rem',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, isLight ? 0.28 : 0.5)}`,
            }}
          >
            C4U
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Loan ops assistant
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.6, maxWidth: 320 }}
          >
            Ask about application counts, journey progress, or a specific applicant. List
            results open on the Applications page with filters applied.
          </Typography>
        </Stack>
      )}

      <Stack spacing={0.85}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', px: 0.25 }}
        >
          Try asking
        </Typography>

        {STARTERS.map((s) => (
          <ButtonBase
            key={s.title}
            onClick={() => onPick(s.prompt)}
            sx={{
              width: 1,
              justifyContent: 'flex-start',
              textAlign: 'left',
              gap: 1.25,
              px: 1.25,
              py: 1.1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.background.paper, 1),
              transition: 'border-color .18s ease, background-color .18s ease, transform .18s ease',
              '&:hover': {
                borderColor: alpha(theme.palette.primary.main, isLight ? 0.45 : 0.6),
                bgcolor: alpha(theme.palette.primary.main, isLight ? 0.05 : 0.12),
                transform: 'translateY(-1px)',
              },
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box
              aria-hidden
              sx={{
                flexShrink: 0,
                width: 30,
                height: 30,
                borderRadius: '9px',
                display: 'grid',
                placeItems: 'center',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, isLight ? 0.1 : 0.2),
              }}
            >
              {s.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                {s.title}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.45 }}
              >
                {s.hint}
              </Typography>
            </Box>
          </ButtonBase>
        ))}
      </Stack>
    </Stack>
  );
}
